import React, { useState } from 'react';
import Menu from '@mui/material/Menu';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ArrowLeft from '@mui/icons-material/ArrowLeft';
import { isSettingsMenuItemVisible } from '../utils/headerSelectors';

type LanguageOption = { code: string; label: string };

const LANGUAGES: ReadonlyArray<LanguageOption> = [
  { code: 'en', label: 'English' },
  { code: 'bg', label: 'Български' },
  { code: 'gr', label: 'Ελληνικά' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' },
  { code: 'ptBr', label: 'Português' },
  { code: 'fr', label: 'Français' },
];

type HeaderAuth = {
  roles?: Array<{ authority?: string | null }> | null;
};

type HeaderFeatureFlags = {
  featureFlagsArray?: string[];
};

type ProfileMenuClasses = {
  documentationProfileItem: string;
};

type ProfileMenuProps = {
  anchorEl: HTMLElement | null;
  auth: HeaderAuth;
  classes: ProfileMenuClasses;
  featureFlags: HeaderFeatureFlags | null | undefined;
  currentLanguage?: string;
  isOpen: boolean;
  onClose: () => void;
  onDocsClick: () => void;
  onLanguageSelect: (language: string) => void;
  onProfileClick: () => void;
  onSettingsClick: () => void;
  onSignOutClick: () => void;
  t: (key: string) => string;
};

export function ProfileMenu({
  anchorEl,
  auth,
  classes,
  currentLanguage,
  featureFlags,
  isOpen,
  onClose,
  onDocsClick,
  onLanguageSelect,
  onProfileClick,
  onSettingsClick,
  onSignOutClick,
  t,
}: ProfileMenuProps): JSX.Element {
  const [languageAnchorEl, setLanguageAnchorEl] = useState<HTMLElement | null>(null);

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setLanguageAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = (): void => {
    setLanguageAnchorEl(null);
  };

  const handleClose = (): void => {
    handleLanguageMenuClose();
    onClose();
  };

  const handleLanguageItemClick = (code: string): void => {
    handleLanguageMenuClose();
    onLanguageSelect(code);
    onClose();
  };

  return (
    <>
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isOpen}
        onClose={handleClose}
      >
        <ListItem
          className={classes.documentationProfileItem}
          button
          onClick={onDocsClick}
          onMouseEnter={handleLanguageMenuClose}
        >
          <ListItemText primary={t('documentation_title')} />
        </ListItem>
        <ListItem button onClick={onProfileClick} onMouseEnter={handleLanguageMenuClose}>
          <ListItemText primary={t('link_profile')} />
        </ListItem>
        {isSettingsMenuItemVisible(auth, featureFlags) && (
          <ListItem button onClick={onSettingsClick} onMouseEnter={handleLanguageMenuClose}>
            <ListItemText primary={t('settings_page_header')} />
          </ListItem>
        )}
        <ListItem button onClick={handleLanguageMenuOpen} onMouseEnter={handleLanguageMenuOpen}>
          <ArrowLeft
            fontSize="small"
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              pointerEvents: 'none',
            }}
          />
          <ListItemText primary={t('link_language')} />
        </ListItem>
        <ListItem button onClick={onSignOutClick} onMouseEnter={handleLanguageMenuClose}>
          <ListItemText primary={t('link_sign_out')} />
        </ListItem>
      </Menu>
      <Menu
        id="profile-language-menu"
        anchorEl={languageAnchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(languageAnchorEl)}
        onClose={handleLanguageMenuClose}
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          onMouseLeave: handleLanguageMenuClose,
          sx: { pointerEvents: 'auto' },
        }}
      >
        {LANGUAGES.map(
          (language: LanguageOption): JSX.Element => (
            <ListItem
              button
              key={language.code}
              selected={language.code === currentLanguage}
              onClick={(): void => handleLanguageItemClick(language.code)}
            >
              <ListItemText primary={language.label} />
            </ListItem>
          )
        )}
      </Menu>
    </>
  );
}
